import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ArrowRight, CheckCircle, Users, Brain, Target, Mail, User as UserIcon } from 'lucide-react';
import { Particles } from '@/components/ui/particles';
import KipPlayer from './KipPlayer';
import { useKiprData } from '@/lib/kiprData';

type DemoState = 'landing' | 'demo' | 'leadCapture' | 'complete';

interface LeadData {
  name: string;
  email: string;
  role: string;
}

const DemoLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { captureLead } = useKiprData();
  const [demoState, setDemoState] = useState<DemoState>('landing');
  const [leadData, setLeadData] = useState<LeadData>({
    name: '',
    email: '',
    role: 'ABA Therapist'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);

  const handleStartDemo = () => {
    setDemoState('demo');
  };

  const handleDemoComplete = () => {
    setDemoState('leadCapture');
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLeadError(null);

    try {
      await captureLead(leadData);
      setDemoState('complete');
      // Navigate to report after a brief delay
      setTimeout(() => {
        navigate('/report/1');
      }, 2000);
    } catch (err) {
      setLeadError(err instanceof Error ? err.message : 'We could not submit your details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof LeadData, value: string) => {
    setLeadData(prev => ({ ...prev, [field]: value }));
  };

  // Demo Player View
  if (demoState === 'demo') {
    return <KipPlayer kipId="1" onDemoComplete={handleDemoComplete} />;
  }

  // Lead Capture Form View
  if (demoState === 'leadCapture') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Great Job!</h2>
              <p className="text-gray-600">
                You've completed your first Kipr training session. Get your detailed performance report by providing your contact information.
              </p>
            </div>

            <form onSubmit={handleLeadSubmit} className="space-y-4">
              {leadError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {leadError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    required
                    value={leadData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="email"
                    required
                    value={leadData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter your email address"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={leadData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="ABA Therapist">ABA Therapist</option>
                  <option value="BCBA">BCBA</option>
                  <option value="Student">Student</option>
                  <option value="Supervisor">Supervisor</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !leadData.name || !leadData.email}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Get My Performance Report</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              We'll never spam you. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success View
  if (demoState === 'complete') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center px-6">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Redirecting you to your detailed performance report...
          </p>
          <div className="w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Landing Page View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white overflow-hidden">
      <Particles
        className="absolute inset-0"
        quantity={100}
        ease={70}
        color="#ffffff"
        size={0.6}
        staticity={40}
      />
      
      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Column - Hero Content */}
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-sm">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  <span>Free Interactive Demo</span>
                </div>
                
                <h1 className="text-4xl lg:text-6xl font-bold font-poppins leading-tight">
                  Master ABA Skills with
                  <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"> AI Training</span>
                </h1>
                
                <p className="text-xl text-slate-300 leading-relaxed">
                  Practice challenging parent conversations in a safe environment. Get instant feedback on your communication skills, empathy, and clinical accuracy.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">Realistic AI-powered parent conversations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">Detailed performance feedback & scoring</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">Safe practice environment - no real consequences</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleStartDemo}
                  className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-full shadow-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-purple-500/30 flex items-center justify-center space-x-3"
                >
                  <Play className="w-6 h-6" />
                  <span>Try Free Demo</span>
                </button>
                
                <button
                  onClick={() => navigate('/explained')}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full font-medium hover:bg-white/20 transition-all duration-300 flex items-center justify-center space-x-3"
                >
                  <span>Learn More</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              <div className="pt-4">
                <p className="text-slate-400 text-sm">
                  ✨ No credit card required • 5-minute demo • Instant feedback
                </p>
              </div>
            </div>

            {/* Right Column - Demo Preview */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold mb-2">Demo Preview</h3>
                    <p className="text-slate-300 text-sm">
                      Practice explaining ABA basics to Allison Thompson
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-lg">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold">Allison Thompson</div>
                        <div className="text-sm text-slate-400">Analytical mother, healthcare background</div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-lg">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                        <Brain className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold">Billy Thompson</div>
                        <div className="text-sm text-slate-400">8 years old, Autism Spectrum Disorder</div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-lg">
                      <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold">Training Goal</div>
                        <div className="text-sm text-slate-400">Explain ABA principles clearly & empathetically</div>
                      </div>
                    </div>
                  </div>

                  <div className="text-center pt-4">
                    <div className="inline-flex items-center space-x-2 text-sm text-slate-400">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      <span>~5 minutes • Voice conversation • Instant feedback</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating elements for visual appeal */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full opacity-20 animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 rounded-full opacity-20 animate-pulse delay-1000"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoLandingPage;